import { useNavigate } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TextField } from '@mui/material';
import { ITechsupportInfo } from '../../interface/techsupport';
import '../styles/page.css';
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import Loader from 'components/Loader';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { IFile } from '../../interface/common';
import {
  FILE_EXE_EXT_REGEX,
  FILE_SINGLE_SIZE_MAX_LIMIT,
  FILE_TOTAL_SIZE_MAX_LIMIT,
} from '../constants';
import JoditEditor, { Jodit } from 'jodit-react';
import imageCompression from 'browser-image-compression';

function TechnicalSupport() {
  const navigate = useNavigate();

  const fileRef = useRef<HTMLInputElement>(null);
  const [dataFile, setDataFile] = useState<IFile[]>();

  const inputRef1 = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);
  const inputRef3 = useRef<HTMLInputElement>(null);
  const inputRef4 = useRef<HTMLInputElement>(null);
  const inputRef5 = useRef<HTMLInputElement>(null);

  const [isTermOpen, setIsTermOpen] = useState(true);
  const [loading, setLoading] = useState(false);

  const editorRef = useRef<any>(null);
  const [content, setContent] = useState<string>('');

  // jodit-editor 이메일 태그오류 발생처리 위해 주석 후 아래로 대체 >>> 일단 초기 소스 보존
  // const config = useMemo(
  //   () => ({
  //     defaultMode: Jodit.constants.MODE_WYSIWYG,
  //     readonly: false,
  //     disabled: false,
  //     toolbar: true,
  //     showCharsCounter: true, // 글자수
  //     showWordsCounter: true, // 단어수
  //     showXPathInStatusbar: true,
  //     toolbarInlineForSelection: false,
  //     toolbarSticky: true, // 툴바고정
  //     placeholder: '문의내용을 입력하세요.',
  //     width: '100%',
  //     showPlaceholder: true,
  //     enableDragAndDropFileToEditor: true,
  //     addNewLine: false,
  //     removeButtons: ['image', 'file', 'video', 'speechRecognize'],
  //     toolbarAdaptive: false,
  //     defaultActionOnPaste: 'custom', // 붙여넣기 시 HTML 원본 유지
  //     // defaultActionOnPaste: 'insert_only_text',
  //     askBeforePasteHTML: false,
  //     allowedTags: '*',
  //     uploader: {
  //       insertImageAsBase64URI: true,
  //     },
  //     events: {
  //       async onPaste(e: ClipboardEvent) {
  //         const items = Array.from(e.clipboardData?.items || []);

  //         for (let item of items) {
  //           if (item.type.indexOf('image') !== -1) {
  //             const file = item.getAsFile();
  //             if (!file) return;

  //             const options = {
  //               maxSizeMB: 0.1,
  //               maxWidthOrHeight: 600,
  //               useWebWorker: true,
  //               fileType: 'image/jpeg',
  //               initialQuality: 0.7,
  //             };
  //             try {
  //               const compressedFile = await imageCompression(file, options); // 압축 실행
  //               const base64 = await imageCompression.getDataUrlFromFile(compressedFile); // base64로 변환

  //               // 에디터에 이미지 삽입
  //               (editorRef.current as any)?.editor.selection.insertHTML(
  //                 `<img src="${base64}" style="max-width:100%; height:auto; display:block;" />`,
  //               );

  //               e.preventDefault(); // 기본 붙여넣기 방지
  //             } catch (err) {
  //               console.error('이미지 압축 실패:', err);
  //             }
  //           }
  //         }
  //       },
  //     },
  //   }),
  //   [],
  // );

  const config = useMemo(
    () => ({
      defaultMode: Jodit.constants.MODE_WYSIWYG,
      readonly: false,
      disabled: false,
      toolbar: true,
      toolbarSticky: true,
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: true,
      toolbarInlineForSelection: false,
      placeholder: '문의내용을 입력하세요.',
      width: '100%',
      showPlaceholder: true,
      addNewLine: false,
      removeButtons: ['image', 'file', 'video', 'speechRecognize'],
      toolbarAdaptive: false,
      enableDragAndDropFileToEditor: false,
      uploader: { insertImageAsBase64URI: false },
      defaultActionOnPaste: 'custom',
      askBeforePasteHTML: false,
      allowedTags: '*',
      disablePlugins: ['paste', 'drag-and-drop'],

      events: {
        afterInit: (ed: any) => {
          if ((ed as any).__bound) ed.events.off('.custom');
          (ed as any).__bound = true;

          const insertHTML = (html: string) =>
            (ed.s?.insertHTML ?? ed.selection?.insertHTML)?.call(ed.s || ed.selection, html);

          const getFragment = (raw: string) => {
            let html = raw || '';
            const m = /<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/i.exec(html);
            if (m) html = m[1];
            html = html.replace(/^[\s\S]*?<body[^>]*>/i, '').replace(/<\/body>[\s\S]*$/i, '');
            return html;
          };

          const sanitizeKeepStyles = (html: string) => {
            const wrap = document.createElement('div');
            wrap.innerHTML = html;
            wrap
              .querySelectorAll('script,style,iframe,object,embed,link,meta')
              .forEach((n) => n.remove());
            wrap.querySelectorAll<HTMLElement>('*').forEach((el) => {
              const attrs = el.attributes;
              for (let i = attrs.length - 1; i >= 0; i--) {
                const name = attrs[i].name;
                if (/^on/i.test(name)) el.removeAttribute(name);
              }
            });
            return wrap.innerHTML;
          };

          const forceImportantColors = (html: string) => {
            const wrap = document.createElement('div');
            wrap.innerHTML = html;
            wrap.querySelectorAll<HTMLElement>('*').forEach((el) => {
              const s = el.getAttribute('style') || '';
              if (/(color|background-color)\s*:/i.test(s)) {
                const ensure = (prop: string) =>
                  s.replace(
                    new RegExp(`(^|;)\\s*${prop}\\s*:\\s*([^;!]+)(?!\\s*!important)(;|$)`, 'i'),
                    (_m, a, v, z) => `${a} ${prop}:${v} !important${z || ''}`,
                  );
                el.setAttribute(
                  'style',
                  ensure('color').replace(/$/, (v) => ensure('background-color')),
                );
              }
            });
            return wrap.innerHTML;
          };

          const escapeHtml = (s: string) =>
            (s ?? '')
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;');

          // 이메일 호환: CSS 없이도 개행/들여쓰기 보존
          const preWrapEmailSafe = (raw: string) => {
            const esc = (t: string) =>
              (t ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

            const toHtmlLines = (s: string) =>
              s
                .split(/\r?\n/)
                .map((line) => {
                  const withTabs = line.replace(/\t/g, '    ');
                  const convertedLeading = withTabs.replace(/^ +/g, (m) =>
                    '&nbsp;'.repeat(m.length),
                  );
                  return convertedLeading || '&nbsp;';
                })
                .join('<br>');

            const body = toHtmlLines(esc(raw));
            return `
            <div data-k-block="1"
                 style="margin:0;padding:12px;border-radius:6px;background:#f8fafc;box-sizing:border-box;max-width:100%;overflow-x:hidden;line-height:1.6;">
              ${body}
            </div>
            <p><br></p>
          `;
          };

          // 코드 감지
          const looksLikeCodeStrict = (plain: string, html: string) => {
            if (/(<pre|<code|<xmp|<textarea|<table)\b/i.test(html)) return true;
            if (/class\s*=\s*["'][^"']*(hljs|language-|code-block)[^"']*["']/i.test(html))
              return true;
            const text = plain || '';
            if (/```|^ {2,}\S/m.test(text)) return true;
            if (/(^|\s)(const|let|var|function|class|import|export)\b/.test(text)) return true;
            const symbols = (text.match(/[<>{};=()[\]]/g) || []).length;
            const len = text.length || 1;
            const density = symbols / len;
            const lines = text.split(/\r?\n/);
            const longLinesWithIndent = lines.filter((l) => /^\s{2,}\S/.test(l)).length;
            return density > 0.08 || longLinesWithIndent >= 3;
          };

          if (!(ed as any).__wrapCssInjected) {
            (ed as any).__wrapCssInjected = true;
            const css = `
            .jodit-wysiwyg pre,
            .jodit-wysiwyg code { white-space: pre-wrap; word-break: break-word; overflow-wrap: anywhere; }
            .jodit-wysiwyg img { max-width: 100%; height: auto; display: block; }
          `;
            const st = ed.editorDocument.createElement('style');
            st.textContent = css;
            ed.editorDocument.head.appendChild(st);
          }

          const imgOptions = {
            maxSizeMB: 0.6,
            maxWidthOrHeight: 1600,
            useWebWorker: true,
            fileType: 'image/jpeg',
            initialQuality: 0.9,
          };

          ed.events.on('dragover.custom', (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          });

          ed.events.on('drop.custom', async (e: DragEvent) => {
            const dt = e.dataTransfer;
            if (!dt) return false;
            e.preventDefault();
            e.stopPropagation();

            const files = dt.files && dt.files.length ? Array.from(dt.files) : [];
            const img = files.find(
              (f) => typeof f.type === 'string' && f.type.startsWith('image/'),
            );
            if (img) {
              try {
                const c = await imageCompression(img, imgOptions);
                const base64 = await imageCompression.getDataUrlFromFile(c);
                insertHTML(
                  `<img src="${base64}" style="max-width:100%;height:auto;display:block;" />`,
                );
              } catch {}
              return false;
            }

            const htmlRaw = typeof dt.getData === 'function' ? dt.getData('text/html') : '';
            const text = typeof dt.getData === 'function' ? dt.getData('text/plain') : '';

            if (htmlRaw) {
              const frag = getFragment(htmlRaw);
              if (looksLikeCodeStrict(text, frag)) {
                insertHTML(preWrapEmailSafe(text || ''));
              } else {
                const safe = forceImportantColors(sanitizeKeepStyles(frag));
                insertHTML(safe);
              }
              return false;
            }

            if (text) insertHTML(preWrapEmailSafe(text));
            return false;
          });

          ed.events.on('paste.custom', async (e: ClipboardEvent) => {
            const dt = e.clipboardData;
            if (!dt) return false;
            e.preventDefault();
            e.stopPropagation();

            const ke = e as any;
            const forceLiteral = !!(ke.shiftKey && (ke.ctrlKey || ke.metaKey));

            const items = dt.items && dt.items.length ? Array.from(dt.items) : [];
            const imgItem = items.find(
              (i) => i.kind === 'file' && typeof i.type === 'string' && i.type.startsWith('image/'),
            );
            if (imgItem) {
              const f = imgItem.getAsFile();
              if (f) {
                try {
                  const c = await imageCompression(f, imgOptions);
                  const base64 = await imageCompression.getDataUrlFromFile(c);
                  insertHTML(
                    `<img src="${base64}" style="max-width:100%;height:auto;display:block;" />`,
                  );
                } catch {}
              }
              return false;
            }

            const htmlRaw = dt.getData('text/html') || '';
            const text = dt.getData('text/plain') || '';

            // HTML이 있어도 "코드처럼 보이면" 이메일-세이프 텍스트로 변환
            if (htmlRaw && !forceLiteral) {
              const frag = getFragment(htmlRaw);
              if (looksLikeCodeStrict(text, frag)) {
                insertHTML(preWrapEmailSafe(text || ''));
              } else {
                const safe = forceImportantColors(sanitizeKeepStyles(frag));
                insertHTML(safe);
              }
              return false;
            }

            // HTML이 없으면 항상 이메일-세이프 텍스트로
            insertHTML(preWrapEmailSafe(text || ''));
            return false;
          });

          ed.events.on('afterSetMode.custom', (mode: number) => {
            const MODE_SOURCE =
              (Jodit as any)?.constants?.MODE_SOURCE ?? (Jodit as any)?.MODE_SOURCE ?? 2;
            if (mode === MODE_SOURCE) {
              const ta = ed.container?.querySelector(
                '.jodit-source__textarea',
              ) as HTMLTextAreaElement | null;
              if (ta) {
                ta.setAttribute('wrap', 'soft');
                ta.style.whiteSpace = 'pre-wrap';
                ta.style.wordBreak = 'break-word';
                ta.style.overflowWrap = 'anywhere';
                ta.style.boxSizing = 'border-box';
                ta.style.maxWidth = '100%';
              }
            }
          });
        },
      },
    }),
    [],
  );

  /** Form Validataion */
  const schema = z.object({
    compNm: z
      .string()
      .trim()
      .min(1, { message: '고객사명을 입력해 주세요.' })
      .max(100, { message: '최대 글자수(100자)를 초과하였습니다.' }),
    projectNm: z
      .string()
      .trim()
      .min(1, { message: '프로젝트명을 입력해 주세요.' })
      .max(100, { message: '최대 글자수(100자)를 초과하였습니다.' }),
    email: z
      .string()
      .trim()
      .min(1, { message: '이메일을 입력해 주세요.' })
      .max(100, { message: '최대 글자수(100자)를 초과하였습니다.' })
      .email({ message: '올바른 형식의 이메일이 아닙니다.' }),
    mobileNo: z
      .string()
      .trim()
      .min(1, { message: '연락처를 입력해 주세요.' })
      .max(11, { message: '최대 글자수(11자)를 초과하였습니다.' })
      .regex(
        RegExp(/^(01[0]{1})[0-9]{4}[0-9]{4}$/),
        '올바른 형식의 휴대폰번호가 아닙니다( - 기호 없음)',
      ),
    serviceCd: z.string(),
    title: z
      .string()
      .trim()
      .min(1, { message: '문의제목을 입력해 주세요.' })
      .max(100, { message: '최대 글자수(100자)를 초과하였습니다.' }),
    content: z.string().trim(),
    reqAttachCd: z.string(),
    agreeYn: z.string(),
    statusCd: z.string(),
  });

  /** Hook */
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      compNm: '',
      projectNm: '',
      email: '',
      mobileNo: '',
      serviceCd: '',
      title: '',
      content: '',
      reqAttachCd: '',
      agreeYn: '',
      statusCd: '',
    },
  });

  const { control, handleSubmit, getValues, setValue } = form;

  // 제품선택
  const [checked, setChecked] = useState<string>('01');
  const handleServiceCheckBoxChange = (checked: boolean, item: string) => {
    if (checked && item) {
      setValue('serviceCd', item);
      setChecked(item);
    }
  };

  // jodit editor HTML 태그 제거 함수
  const isContentEmpty = (html: string): boolean => {
    // 태그 제거 후 공백만 남는지 확인
    const text = html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, '')
      .trim();
    return text.length === 0;
  };

  const handleEditorChange = (newContent: string) => {
    if (isContentEmpty(newContent)) setContent('');
    else setContent(newContent);
  };

  function isChecked(serviceNm: string): boolean {
    if (checked) return checked.indexOf(serviceNm) !== -1;
    return false;
  }

  // 저장 및 이메일 발송
  const onSubmit = async () => {
    if (loading) return; // 중복 클릭 방지

    if (getValues('agreeYn') === '' || getValues('agreeYn') === 'N') {
      alert('개인정보 수집 및 이용동의 약관에 동의해주세요.');
      return;
    }

    const result = confirm('내용을 문의하시겠습니까?');
    if (!result) return;

    setLoading(true);

    try {
      // 첨부파일 신규 저장
      if (dataFile?.[0]?.file) {
        const fileData = new FormData();
        fileData.append('file', dataFile[0].file);

        const uploadRes = await fetch('/common/files/upload', {
          method: 'POST',
          body: fileData,
        });

        if (!uploadRes.ok) {
          alert('첨부파일 업로드에 실패하여 문의하기를 중단합니다.');
          return;
        }

        const uploadJson = await uploadRes.json();
        const attachCd = uploadJson?.data?.attachCd ?? '';
        if (!attachCd) {
          alert('첨부파일 코드가 확인되지 않아 문의하기를 중단합니다.');
          return;
        }
        setValue('reqAttachCd', attachCd);
      } else {
        setValue('reqAttachCd', '');
      }

      // 문의 내용 저장 (첨부 성공 or 미첨부 시 진행)
      const formData: ITechsupportInfo = {
        compNm: getValues('compNm'), // 고객사명
        projectNm: getValues('projectNm'), // 프로젝트명
        email: getValues('email'), // 이메일
        mobileNo: getValues('mobileNo'), // 연락처
        serviceCd: checked, // 제품버전
        title: getValues('title'), // 문의제목
        content: content, // 문의내용
        reqAttachCd: getValues('reqAttachCd'), // 첨부파일 코드
        agreeYn: getValues('agreeYn'), // 동의여부
        statusCd: '01', // 01: 처리중
      };

      const saveRes = await fetch('/techsupport/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(formData),
      });

      if (!saveRes.ok) {
        alert('기술지원 문의에 실패하였습니다.');
        return;
      }

      await saveRes.json();
      alert('문의하신 내용이 전송되었습니다.\n담당자로부터 빠른 시일내에 답변드릴 예정입니다.');
      navigate(0);
    } catch (err) {
      console.error(err);
      alert('처리중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAgreeCheckBoxChange = (checked: boolean, value: string) => {
    if (checked) setValue('agreeYn', 'Y');
    else setValue('agreeYn', 'N');
  };

  const handleTermOpen = () => {
    switch (isTermOpen) {
      case true:
        setIsTermOpen(false);
        break;

      case false:
        setIsTermOpen(true);
        break;

      default:
        break;
    }
  };

  const handleBlur = (targetValue: string | number, targetName: string) => {
    // 엔터키로 포커스 이동 시 공백이면 reset
    const valLength = Number(targetValue.toString().trim().length);

    if (valLength === 0) {
      switch (targetName) {
        case 'compNm':
          if (valLength === 0) setValue('compNm', '');
          break;

        case 'projectNm':
          if (valLength === 0) setValue('projectNm', '');
          break;

        case 'email':
          if (valLength === 0) setValue('email', '');
          break;

        case 'mobileNo':
          if (valLength === 0) setValue('mobileNo', '');
          break;

        case 'serviceCd':
          if (valLength === 0) setValue('serviceCd', '');
          break;

        case 'title':
          if (valLength === 0) setValue('title', '');
          break;

        case 'content':
          if (valLength === 0) setValue('content', '');
          break;

        default:
          break;
      }
    }
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLDivElement> & React.ChangeEvent<HTMLInputElement>,
  ) => {
    const name = e.target.name;
    const len = e.target.value.length;
    const charCode = e.key;

    const isNumber = /[0-9]/.test(charCode);
    const jodit = editorRef.current;

    if (charCode === 'Enter') {
      e.preventDefault();
      if (e.target === inputRef1.current) inputRef2.current?.focus();
      else if (e.target === inputRef2.current) inputRef3.current?.focus();
      else if (e.target === inputRef3.current) inputRef4.current?.focus();
      else if (e.target === inputRef4.current) inputRef5.current?.focus();
      else if (e.target === inputRef5.current) jodit.focus();
    }

    switch (name) {
      case 'mobileNo':
        if (len >= 11 && isNumber) {
          e.preventDefault();
        }
        break;
    }
  };

  const getOnlyNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.replace(/[^0-9]/g, '');
  };

  /** 첨부파일 버튼 클릭 이벤트 */
  const handleAddFile = () => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  };

  /** 첨부파일 이벤트 */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileInfo = fileRef.current!.files?.[0];
    if (!fileInfo) {
      setDataFile([]);
      return false;
    }

    // 유효성체크(확장자)
    const fileNm = fileInfo.name;
    const fileChk01 = fileExtChk(fileNm, 'exe');

    if (fileChk01 && !fileChk01.isOk) {
      fileRef.current!.value = '';
      alert(fileChk01.message);
      return;
    }

    // 유효성체크(용량)
    const fileChk02 = fileSizeChk(fileInfo.size, 'single');
    if (!fileChk02.isOk) {
      fileRef.current!.value = '';
      alert(fileChk02.message);
      return;
    }

    const newFile: IFile = {
      key: fileInfo.lastModified,
      fileNm: fileInfo.name,
      fileSize: fileInfo.size,
      file: fileInfo,
    };
    setDataFile([newFile]);

    e.target.value = '';
  };

  /**
   * 첨부파일 삭제 버튼 클릭 이벤트
   */
  const handleDelFile = async () => {
    setDataFile([]);
  };

  // 파일 사이즈 체크
  const fileSizeChk = (size: number, type: 'single' | 'total') => {
    let isOk = true;
    let message = '';

    if (type === 'single' && size > FILE_SINGLE_SIZE_MAX_LIMIT) {
      const maxSize = FILE_SINGLE_SIZE_MAX_LIMIT / 1024 / 1024;
      isOk = false;
      message = `${maxSize}MB 이상 파일은 첨부할 수 없습니다.`;
    }

    if (type === 'total' && size > FILE_TOTAL_SIZE_MAX_LIMIT) {
      const maxSize = FILE_TOTAL_SIZE_MAX_LIMIT / 1024 / 1024;
      isOk = false;
      message = `첨부할 수 있는 파일의 최대 용량은 ${maxSize}MB 입니다.`;
    }

    return { isOk, message };
  };

  // 파일 확장자 체크
  const fileExtChk = (fileName: string, type: 'exe') => {
    const ext = fileName.split('.').pop()?.toLocaleLowerCase();

    let isOk = true;
    let message = '';

    if (ext) {
      if (type === 'exe' && FILE_EXE_EXT_REGEX.test(ext)) {
        isOk = false;
        message = '업로드 할 수 없는 확장자가 포함되어 있습니다.';
      }

      return { isOk, message };
    }
  };

  useEffect(() => {
    const firstErrorField = Object.keys(form.formState.errors)[0];

    if (firstErrorField) {
      const field = document.querySelector(`[name="${firstErrorField}"]`);

      if (field) {
        (field as HTMLElement).focus();
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [form.formState.errors]);

  useEffect(() => {
    AOS.init();
    inputRef1.current?.focus();
  }, []);

  return (
    <>
      {loading ? <Loader /> : null}
      <FormProvider {...form}>
        <section id="contact-banner">
          <div className="page-banner-bg">
            <div className="m-auto page-banner-container">
              <h2 data-aos="fade-right" data-aos-duration="700" data-aos-once="true">
                기술지원
              </h2>
              <p
                className="color-caption fs-base"
                data-aos="fade-right"
                data-aos-duration="700"
                data-aos-once="true"
                data-aos-delay="200"
              >
                문의를 남겨주시면 담당자가 빠른 시일내에 회신 드릴 예정입니다.
              </p>
            </div>
          </div>
        </section>

        <main id="question">
          <div className="question-container m-auto">
            <div className="question-content">
              <div className="question-item">
                <p className="fs-md fw-bold">제품선택</p>
              </div>
              <div className="question-product">
                <ul>
                  <li>
                    <input
                      type="checkbox"
                      id="01"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('01')}
                    />
                    <label htmlFor="01" className="product-select">
                      <img className="img-24" src="./images/logo-uxbooster.svg" alt="" />
                      <p className="ml-8 fw-bold mt-4 color-caption">for Nexacro</p>
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="02"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('02')}
                    />
                    <label htmlFor="02" className="product-select">
                      <img className="img-24" src="./images/logo-uxbooster.svg" alt="" />
                      <p className="ml-8 fw-bold mt-4 color-caption">for React</p>
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="03"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('03')}
                    />
                    <label htmlFor="03" className="product-select">
                      <img className="img-24" src="./images/logo-uxboard.svg" alt="" />
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="04"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('04')}
                    />
                    <label htmlFor="04" className="product-select">
                      <img className="img-24" src="./images/logo-uxcreator.svg" alt="" />
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="05"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('05')}
                    />
                    <label htmlFor="05" className="product-select">
                      <img className="img-28" src="./images/logo-aichanger.svg" alt="" />
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="06"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('06')}
                    />
                    <label htmlFor="06" className="product-select">
                      <img className="img-28 mb-8" src="./images/logo-gems.svg" alt="" />
                    </label>
                  </li>
                  <li>
                    <input
                      type="checkbox"
                      id="07"
                      onChange={(e) => {
                        handleServiceCheckBoxChange(e.target.checked, e.target.id);
                      }}
                      checked={isChecked('07')}
                    />
                    <label htmlFor="07" className="product-select">
                      <img className="img-28 mb-4" src="./images/logo-epro.svg" alt="" />
                    </label>
                  </li>
                </ul>
              </div>
            </div>
            <div className="question-line"></div>
            <div className="question-content">
              <div className="question-item">
                <p className="fs-md fw-bold">정보입력</p>
              </div>
              <div className="support-write">
                <ul>
                  <li>
                    <label className="fs-base essential">고객사</label>
                    <div>
                      <Controller
                        name="compNm"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef1}
                            placeholder="고객사명을 입력해주세요."
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                  <li>
                    <label className="fs-base essential">프로젝트명</label>
                    <div>
                      <Controller
                        name="projectNm"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef2}
                            placeholder="프로젝트명을 입력해주세요."
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                </ul>
                <ul>
                  <li>
                    <label className="fs-base essential">이메일</label>
                    <div>
                      <Controller
                        name="email"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef3}
                            placeholder="이메일 주소를 입력하세요."
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                  <li>
                    <label className="fs-base essential">연락처</label>
                    <div>
                      <Controller
                        name="mobileNo"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef4}
                            type="tel"
                            placeholder="휴대폰번호를 입력하세요. (예. 01012345678)"
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onInput={getOnlyNumber}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div className="question-content">
              <div className="question-item"></div>
              <div className="question-write">
                <ul>
                  <li>
                    <label className="fs-base essential">제목</label>
                    <div>
                      <Controller
                        name="title"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            inputRef={inputRef5}
                            maxRows={2}
                            placeholder="제목을 입력하세요."
                            sx={{ width: '100%' }}
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            onKeyDown={handleKeyDown}
                            onBlur={(e) => handleBlur(e.target.value, e.target.name)}
                          />
                        )}
                      />
                    </div>
                  </li>
                  <li>
                    <label className="fs-base">내용</label>
                    <div className="jodit-edit">
                      {/* <JoditEditor
                        ref={editorRef}
                        value={content}
                        config={config as any}
                        onBlur={handleEditorChange}
                      /> */}
                      <JoditEditor
                        ref={editorRef}
                        value={content}
                        config={config as any}
                        onBlur={(v) => setContent(isContentEmpty(v) ? '' : v)}
                      />
                    </div>
                  </li>
                  <li>
                    <label className="fs-base">첨부파일</label>
                    <div className="support-file-box">
                      <div className="support-file-area">
                        <input
                          type="file"
                          ref={(el) => {
                            fileRef.current = el;
                          }}
                          className="support-file-name color-placeholder"
                          style={{ display: 'none' }}
                          onChange={handleFileChange}
                        />
                        {dataFile && dataFile.length > 0 ? (
                          <>
                            {dataFile[0].fileNm}
                            <button className="file-cancel" onClick={handleDelFile}></button>
                          </>
                        ) : (
                          <p className="support-file-name color-placeholder">
                            한 개의 파일만 첨부 가능합니다. (최대 10MB)
                          </p>
                        )}
                      </div>
                      <button
                        className="file-attach"
                        type="button"
                        // onClick={isFile ? handleDelFile : handleAddFile}
                        onClick={handleAddFile}
                      >
                        {/* {isFile ? `파일삭제` : `파일첨부`} */}
                        파일첨부
                      </button>
                    </div>
                    <div className="fs-xs pl-24 file-infotext">
                      여러 파일 첨부 시 압축 파일을 첨부해주세요.
                    </div>
                  </li>
                </ul>
                <div className="privacy">
                  <div>
                    <input
                      type="checkbox"
                      id="agree"
                      onChange={(e) => {
                        handleAgreeCheckBoxChange(e.target.checked, e.target.value);
                      }}
                    />
                    <label htmlFor="agree"> 개인정보 수집 및 이용에 동의합니다. </label>
                  </div>
                  <button
                    type="button"
                    className="privacy-toggle-btn"
                    id="toggleBtn"
                    onClick={handleTermOpen}
                  >
                    {isTermOpen ? '내용닫기' : '내용보기'}
                  </button>
                </div>
                <div>
                  {isTermOpen ? (
                    <div className="" id="contentWrapper">
                      <div className="privacy-content">
                        <div>
                          <strong>개인정보의 처리 목적</strong>
                          <p className="mt-8">
                            회사는 수집한 개인정보를 다음 목적을 위해 활용합니다.
                          </p>
                          <p className="ml-12">
                            - 제품 기술 지원을 위한 고객의 정보 수집
                          </p>
                        </div>
                        <div>
                          <strong>개인정보 수집항목 및 수집 방법</strong>
                          <p className="mt-8">
                            회사는 고객문의에 활용하기 위한 수집항목과 수집방법은 아래와 같습니다.
                          </p>
                          <p className="ml-12">
                            가. 수집항목 : 고객사명, 이메일, 연락처
                            <br />
                            나. 수집방법 : 홈페이지
                          </p>
                        </div>
                        <div>
                          <strong>개인정보의 처리 및 보유기간</strong>
                          <p className="mt-8">
                            기술지원에 활용하기 위해 수집된 개인정보의 처리 및 보유기간은 아래와
                            같습니다.
                          </p>
                          <p className="ml-12">
                            - 보존항목 : 기술지원을 위한 고객의 정보
                            <br />- 보존기간 : 수집 후 5년
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    ''
                  )}
                </div>
                <button className="btn-submit fs-base fw-bold" onClick={handleSubmit(onSubmit)}>
                  문의하기
                </button>
              </div>
            </div>
          </div>
        </main>
      </FormProvider>
    </>
  );
}
export default TechnicalSupport;
